import React, { useState, useEffect } from 'react';
import Sidebar from './components/Sidebar';
import Dashboard from './components/Dashboard';
import AddReflection from './components/AddReflection';
import AllReflections from './components/AllReflections';
import CommunityStories from './components/CommunityStories';
import FeelingInsights from './components/FeelingInsights';
import MyPosts from './components/MyPosts';
import TeacherList from './components/TeacherList';
import ExerciseList from './components/ExerciseList';
import PhilosophyDetail from './components/PhilosophyDetail';
import PhilosophyItem from './components/PhilosophyItem';
import DataTest from './components/DataTest';
import { AuthProvider } from './contexts/AuthContext';
import { NotificationProvider } from './contexts/NotificationContext';
import ConfigService from './utils/configService';

function AppContent() {
  const [activeView, setActiveView] = useState('dashboard');
  const [reflections, setReflections] = useState([]);
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);

  // 默认用户（演示用）
  const currentUser = ConfigService.useServerData() ? '当前用户' : '瑜伽爱好者';

  // 测试服务器连接（如果启用了服务器模式）
  useEffect(() => {
    const testConnection = async () => {
      if (ConfigService.useServerData()) {
        console.log('🔗 服务器模式已启用，测试连接...');
        console.log(`📊 配置信息:`, ConfigService.getConfigInfo());

        try {
          const result = await ConfigService.testServerConnection();
          if (result.success) {
            console.log('✅ 服务器连接成功');
          } else {
            console.error('❌ 服务器连接失败:', result.message);
            console.error('💡 请确保Django服务器正在运行: cd yogaflow && python manage.py runserver');
          }
        } catch (error) {
          console.error('❌ 服务器连接测试失败:', error.message);
          console.error('💡 请确保Django服务器正在运行: cd yogaflow && python manage.py runserver');
        }
      } else {
        console.log('📁 本地文件模式已启用');
      }
    };

    testConnection();
  }, []);


  // 初始化数据 - 从yogaData.json读取
  useEffect(() => {
    // 从localStorage加载用户添加的反思数据
    const savedReflections = localStorage.getItem('yogaReflections');
    if (savedReflections) {
      try {
        const parsedReflections = JSON.parse(savedReflections);
        setReflections(parsedReflections);
      } catch (error) {
        console.error('Error parsing saved reflections:', error);
        setReflections([]);
      }
    } else {
      setReflections([]);
    }
  }, []);

  // 保存数据到 localStorage
  useEffect(() => {
    localStorage.setItem('yogaReflections', JSON.stringify(reflections));
  }, [reflections]);

  useEffect(() => {
    const previousBodyOverflow = document.body.style.overflow;
    const previousHtmlOverflow = document.documentElement.style.overflow;

    if (isSidebarOpen) {
      document.body.style.overflow = 'hidden';
      document.documentElement.style.overflow = 'hidden';
    }

    return () => {
      document.body.style.overflow = previousBodyOverflow;
      document.documentElement.style.overflow = previousHtmlOverflow;
    };
  }, [isSidebarOpen]);

  const addReflection = (reflection) => {
    const newReflection = {
      id: Date.now(),
      user: currentUser,
      ...reflection,
      createdAt: new Date().toISOString()
    };
    setReflections([newReflection, ...reflections]);
    setActiveView('dashboard'); // 提交后跳转回仪表板
  };

  const renderActiveView = () => {
    switch (activeView) {
      case 'dashboard':
        return <Dashboard setActiveView={setActiveView} />;
      case 'community-stories':
        return <CommunityStories />;
      case 'feeling-insights':
        return <FeelingInsights />;

      case 'add-reflection':
        return <AddReflection onSubmit={addReflection} currentUser={currentUser} />;
      case 'reflections-list':
        return <AllReflections />;
      case 'teacher-list':
        return <TeacherList />;
      case 'exercise-list':
        return <ExerciseList />;
      case 'philosophy-detail':
        return <PhilosophyDetail />;
      case 'philosophy-find-your-breath':
        return <PhilosophyItem philosophyName="find-your-breath" />;
      case 'philosophy-trust-the-process':
        return <PhilosophyItem philosophyName="trust-the-process" />;
      case 'philosophy-practice-with-love':
        return <PhilosophyItem philosophyName="practice-with-love" />;
      case 'philosophy-embrace-the-journey':
        return <PhilosophyItem philosophyName="embrace-the-journey" />;
      case 'data-test':
        return <DataTest />;
      case 'my-posts':
        return <MyPosts />;

      default:
        return <Dashboard setActiveView={setActiveView} />;
    }
  };

  return (
    <div className="flex min-h-screen bg-gradient-to-br from-sage-50 to-warm-50">
      {/* Mobile App Bar */}
      {!isSidebarOpen && (
        <header className="lg:hidden fixed inset-x-0 top-0 z-30 border-b border-sage-200/70 bg-white/85 backdrop-blur-md shadow-soft">
          <div className="flex h-16 items-center gap-3 px-4">
            <button
              type="button"
              aria-label="打开菜单"
              onClick={() => setIsSidebarOpen(true)}
              className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-sage-600 text-white shadow-soft transition-colors hover:bg-sage-700 active:bg-sage-800"
            >
              <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 7h16M4 12h16M4 17h16" />
              </svg>
            </button>
            <div className="min-w-0">
              <h1 className="truncate text-lg font-serif font-bold leading-tight text-sage-800">
                Ashtanga Flow
              </h1>
              <p className="truncate text-xs text-sage-600">阿斯汤加爱好者社区</p>
            </div>
          </div>
        </header>
      )}

      {/* Overlay for mobile */}
      {isSidebarOpen && (
        <div
          className="lg:hidden fixed inset-0 bg-black bg-opacity-50 z-40 touch-none"
          onClick={() => setIsSidebarOpen(false)}
        />
      )}

      <Sidebar
        activeView={activeView}
        setActiveView={setActiveView}
        isSidebarOpen={isSidebarOpen}
        setIsSidebarOpen={setIsSidebarOpen}
      />
      <main className="flex-1 overflow-y-auto p-4 pt-20 lg:ml-0 lg:p-6">
        {renderActiveView()}
      </main>
    </div>
  );
}

function App() {
  return (
    <NotificationProvider>
      <AuthProvider>
        <AppContent />
      </AuthProvider>
    </NotificationProvider>
  );
}

export default App;
